import axios from 'axios';
import { ElMessage } from 'element-plus';
import config from '@/config.js';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

/**
 * 获取上传签名
 * @param {string} type 文件类型（如 'video', 'image', 'audio', 'cert' 等）
 * @returns {Promise<Object>} 返回签名数据
 */
export const getUploadSignature = async (type = 'file') => {
  try {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}');
    if (!userInfo.id) {
      throw new Error('用户信息中缺少商户ID');
    }
    
    const response = await axios.post(
      `${config.BASE_URL}/shop/api/upload_signature.php`,
      {
        type,
        id: userInfo.id
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // 检查接口返回的错误
    if (response.data && response.data.success === false) {
      // 抛出具体的错误信息
      throw new Error(response.data.error || '获取上传签名失败');
    }

    if (response.data && response.data.success) {
      // 如果是证书类型（本地存储），返回特殊标识
      if (response.data.type === 'local') {
        return {
          type: 'local',
          uploadUrl: response.data.uploadUrl,
          folder: response.data.folder
        };
      }
      
      // OSS上传所需字段
      const requiredFields = ['accessKeyId', 'policy', 'signature', 'bucket', 'endpoint', 'folder'];
      for (const field of requiredFields) {
        if (!response.data[field]) {
          throw new Error(`接口返回数据缺少字段: ${field}`);
        }
      }
      return response.data;
    } else {
      throw new Error(response.data?.error || response.data?.message || "获取上传签名失败");
    }
  } catch (error) {
    console.error("获取上传签名失败:", error);
    // 重新抛出错误，保留原始错误信息
    throw error;
  }
};

/**
 * 上传文件到OSS
 * @param {File} file 要上传的文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<{ success: boolean, url: string, size: number }>} 返回上传结果
 */
export const uploadToOSS = async (file, signatureData) => {
  return new Promise((resolve, reject) => {
    const uniqueFileName = `${uuidv4()}.${file.name.split('.').pop()}`;
    const formData = new FormData();
    formData.append("key", `${signatureData.folder}${uniqueFileName}`);
    formData.append("policy", signatureData.policy);
    formData.append("OSSAccessKeyId", signatureData.accessKeyId);
    formData.append("signature", signatureData.signature);
    formData.append("success_action_status", "200");
    formData.append("file", file);

    axios.post(
      `https://${signatureData.bucket}.${signatureData.endpoint}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then((response) => {
      if (response.status === 200) {
        const fileUrl = `https://${signatureData.bucket}.${signatureData.endpoint}/${signatureData.folder}${uniqueFileName}`;
        resolve({ success: true, url: fileUrl, size: file.size });
      } else {
        reject(new Error("上传失败，状态码：" + response.status));
      }
    })
    .catch((error) => {
      console.error("上传失败:", error);
      // 提取OSS返回的错误信息
      let errorMsg = "上传失败";
      if (error.response && error.response.data) {
        errorMsg = error.response.data.message || error.response.data.error || "上传失败";
      } else if (error.message) {
        errorMsg = error.message;
      }
      reject(new Error(errorMsg));
    });
  });
};

/**
 * 上传证书文件到本地服务器
 * @param {File} file 要上传的文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<{ success: boolean, url: string, name: string, size: number }>} 返回上传结果
 */
export const uploadCertToLocal = async (file, signatureData) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    axios.post(
      `${config.BASE_URL}${signatureData.uploadUrl}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then((response) => {
      if (response.data && response.data.success) {
        resolve({ 
          success: true, 
          url: response.data.url,
          name: response.data.name,
          size: response.data.size
        });
      } else {
        reject(new Error(response.data.error || response.data.message || "上传失败"));
      }
    })
    .catch((error) => {
      console.error("上传失败:", error);
      let errorMsg = "上传失败";
      if (error.response && error.response.data) {
        errorMsg = error.response.data.error || error.response.data.message || "上传失败";
      } else if (error.message) {
        errorMsg = error.message;
      }
      reject(new Error(errorMsg));
    });
  });
};

/**
 * 统一上传入口（自动识别文件类型）
 * @param {File} file 要上传的文件
 * @param {string} type 文件类型
 * @returns {Promise<string>} 返回文件URL
 */
export const uploadFile = async (file, type = 'image') => {
  try {
    const signature = await getUploadSignature(type);
    
    if (type === 'cert' && signature.type === 'local') {
      const result = await uploadCertToLocal(file, signature);
      return result.url;
    } else {
      const result = await uploadToOSS(file, signature);
      return result.url;
    }
  } catch (error) {
    console.error('上传失败:', error);
    // 显示具体的错误信息
    ElMessage.error(error.message || '上传失败');
    throw error;
  }
};

/**
 * 上传文件前的校验
 * @param {File} file 要上传的文件
 * @param {Object} options 校验选项
 * @param {number} options.maxSize 文件最大大小（MB）
 * @returns {Promise<boolean>} 返回校验结果
 */
export const beforeUpload = async (file, options = {}) => {
  const { maxSize } = options;

  if (maxSize && file.size / 1024 / 1024 > maxSize) {
    ElMessage.error(`文件大小不能超过 ${maxSize}MB`);
    return false;
  }

  return true;
};

/**
 * 获取视频/音频时长
 * @param {File} file 视频/音频文件
 * @returns {Promise<number>} 返回视频/音频时长（秒）
 */
export const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const media = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
    media.src = URL.createObjectURL(file);
    media.onloadedmetadata = () => {
      resolve(media.duration);
    };
    media.onerror = () => {
      reject(new Error("无法读取文件时长"));
    };
  });
};